import { ACTION_STATES, FLY_STRIP, JUMP_STRIP, LAND_STRIP, TAKEOFF_STRIP, directionName, flightAltitude, frameIndex, jumpLift, locomotionStrip, type CharacterPlayback, type DirectionName, type Facing, type SheetAnim } from "odyssey-shared";

/**
 * Which strip, direction and frame a character shows this frame (ADR-036 decision 3 and its amendments), the one
 * choice every way of drawing a character shares: the layered sprite (`character.ts`) and the composed one
 * (`looks/look-sprite.ts`).
 *
 * Idle, walk and run come from the character's motion by the shared `locomotionStrip`: the run while it moves with
 * the running bit and its strips have one (ADR-116 decision 14), and otherwise idle or walk, their drawn variants while
 * its weapon is drawn and its strips have them. A strip beyond them (a swing, a hurt, the death held) comes from its playback and shows over motion. A
 * swing's strip (one an ability names) keeps the direction of its first frame for its whole length, whatever the
 * facing does meanwhile (ADR-073's amendment "the way a displacement goes, and a swing keeps its facing"): the state's
 * hold counts inputs, so a watcher's strip, which starts when the bits arrive, could otherwise end on the walk's
 * facing.
 *
 * While the `jump` strip plays, the choice also says how far the drawn character stands above its feet (`lift`, the
 * shared arc of ADR-116's third amendment); every other strip, and a sheet without `jump`, lifts nothing.
 *
 * A sheet with `fly`, `takeoff` and `land` strips flies (P2.22-T38): a moving character with the running bit plays
 * `takeoff` once, then `fly`; when the bit drops or it stops, `land` plays once over where it is, and then motion's
 * strip returns. A character first seen in flight is already in the air, with no take-off, and so is one whose action has just ended while it still flies: an action resets the flight's state. The flight adds an
 * `altitude` of its own to the baked lift of the art (`flight.ts`), and `height` is the two together, which the shadow
 * narrows by; both are the art's pixels.
 *
 * `choose` allocates nothing: a character calls it every frame.
 */
export class StripChoice {
  /** The strip shown. */
  public anim = "idle";
  /** The direction shown. */
  public direction: DirectionName = "down";
  /** The frame shown, its place in the strip; the last one a strip was found for. */
  public frame = 0;
  /** Whether the playback's strip shows; false when motion decides. */
  public playing = false;
  /** When the strip and direction shown began, in scene time, which motion's frames count from. */
  public startMs = 0;
  /** How far the drawn character stands above its feet, in whole world pixels: the hop's arc, 0 off the `jump` strip. */
  public lift = 0;
  /** How far the flight raises the drawn character above its art's own lift, in the art's pixels; 0 on the ground. */
  public altitude = 0;
  /** How far the art stands above its feet line this frame, baked lift and altitude together, in the art's pixels; -1 for a strip that names no lift, which draws no shadow of its own. */
  public height = -1;
  private swingDrawn = -1;
  private air: number = Air.Ground;
  private airStartMs = 0;
  private seen = false;

  /**
   * Chooses what the character shows now.
   *
   * @param strips The strips the character can play, by name; null for a character with nothing to draw, whose
   *   playback is then left alone.
   * @param playback The character's playback.
   * @param facing The way it faces.
   * @param moving Whether it moved since the last frame.
   * @param running Whether it runs: its view's running bit, or the page's prediction for its own character.
   * @param drawn Whether its weapon is drawn.
   * @param nowMs Scene time in ms.
   * @returns The chosen strip's rate and length, or undefined when the character has no such strip, in which case
   *   `frame` keeps its value.
   */
  public choose(strips: Readonly<Record<string, SheetAnim>> | null, playback: CharacterPlayback, facing: Facing, moving: boolean, running: boolean, drawn: boolean, nowMs: number): SheetAnim | undefined {
    const playing = strips !== null && playback.resolve(strips, nowMs);
    const flies = strips !== null && strips[FLY_STRIP] !== undefined && strips[TAKEOFF_STRIP] !== undefined && strips[LAND_STRIP] !== undefined;
    const motion = playing ? (playback.strip as string) : locomotionStrip(strips ?? NO_STRIPS, moving, running, drawn);
    if (playing && flies) {
      this.air = Air.Ground;
      this.seen = false;
    }
    const anim = flies && !playing ? this.flight(strips, motion, moving, nowMs) : motion;
    const swing = playing && ACTION_STATES.has(anim) ? playback.starts : -1;
    const direction = swing !== -1 && swing === this.swingDrawn ? this.direction : directionName(facing);
    this.swingDrawn = swing;
    this.playing = playing;
    if (anim !== this.anim || direction !== this.direction) {
      this.anim = anim;
      this.direction = direction;
      this.startMs = nowMs;
    }
    const strip = strips?.[anim];
    if (strip !== undefined) {
      this.frame = playing ? Math.min(playback.frame, strip.frames - 1) : frameIndex(strip, nowMs - this.startMs);
    }
    this.lift = playing && anim === JUMP_STRIP && strip !== undefined ? jumpLift(this.frame) : 0;
    this.altitude = strip === undefined || !flies ? 0 : flightAltitude(anim, this.frame, strip.frames);
    this.height = strip?.lift === undefined ? -1 : (strip.lift[this.frame] ?? 0) + this.altitude;
    return strip;
  }

  /**
   * The strip a flying character shows: motion's, except the take-off as the flight begins and the landing as it ends,
   * each played through once.
   *
   * @param strips The strips the character can play; all of `fly`, `takeoff` and `land` are among them.
   * @param motion The strip locomotion chose.
   * @param moving Whether the character moved since the last frame.
   * @param nowMs Scene time in ms.
   * @returns The strip to show.
   */
  private flight(strips: Readonly<Record<string, SheetAnim>>, motion: string, moving: boolean, nowMs: number): string {
    const first = !this.seen;
    this.seen = true;
    const takeoff = strips[TAKEOFF_STRIP] as SheetAnim;
    const land = strips[LAND_STRIP] as SheetAnim;
    if (motion === FLY_STRIP) {
      if (this.air === Air.Ground && !first) {
        this.air = Air.Takeoff;
        this.airStartMs = nowMs;
      } else if (this.air === Air.Ground || this.air === Air.Land) {
        this.air = Air.Fly;
      }
      if (this.air === Air.Takeoff && nowMs - this.airStartMs >= (takeoff.frames * 1000) / takeoff.fps) {
        this.air = Air.Fly;
      }
      return this.air === Air.Takeoff ? TAKEOFF_STRIP : FLY_STRIP;
    }
    if (this.air === Air.Takeoff || this.air === Air.Fly) {
      this.air = Air.Land;
      this.airStartMs = nowMs;
    }
    if (this.air === Air.Land) {
      if (moving || nowMs - this.airStartMs >= (land.frames * 1000) / land.fps) {
        this.air = Air.Ground;
        return motion;
      }
      return LAND_STRIP;
    }
    return motion;
  }
}

/**
 * Where a flying character is between the ground and the air: on it, taking off, flying and landing.
 */
const Air = { Ground: 0, Takeoff: 1, Fly: 2, Land: 3 } as const;

/**
 * The strips of a character with nothing to draw.
 */
const NO_STRIPS: Readonly<Record<string, SheetAnim>> = Object.freeze({});
